/**
 * Direct pump.fun API client for frontend
 *
 * Browsers aren't blocked by pump.fun, so we call their API directly.
 * Our API is only used for risk analysis (Helius data).
 */

const API_BASE = 'https://frontend-api-v3.pump.fun';

export interface PumpCoin {
  mint: string;
  name: string;
  symbol: string;
  description: string;
  image_uri: string;
  metadata_uri: string;
  twitter: string | null;
  telegram: string | null;
  website: string | null;
  bonding_curve: string;
  associated_bonding_curve: string;
  creator: string;
  created_timestamp: number;
  raydium_pool: string | null;
  complete: boolean;
  virtual_sol_reserves: number;
  virtual_token_reserves: number;
  total_supply: number;
  king_of_the_hill_timestamp: number | null;
  market_cap: number;
  reply_count: number;
  last_reply: number | null;
  nsfw: boolean;
  usd_market_cap: number;
}

export interface PumpCoinResponse {
  coins: PumpCoin[];
  next_cursor?: string;
}

/**
 * Fetch currently live tokens (on bonding curve)
 */
export async function getCurrentlyLive(limit = 50): Promise<PumpCoin[]> {
  const url = `${API_BASE}/coins/currently-live?limit=${limit}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`pump.fun API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch top performing tokens (king of the hill candidates)
 */
export async function getTopRunners(limit = 20): Promise<PumpCoin[]> {
  const url = `${API_BASE}/coins/top-runners?limit=${limit}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`pump.fun API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Search tokens
 */
export async function searchCoins(searchTerm: string, limit = 20): Promise<PumpCoinResponse> {
  const url = new URL(`${API_BASE}/coins/search`);
  url.searchParams.set('searchTerm', searchTerm);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('sortBy', 'market_cap');
  url.searchParams.set('order', 'DESC');

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`pump.fun API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Get single token by mint address
 */
export async function getCoinByMint(mint: string): Promise<PumpCoin | null> {
  const url = `${API_BASE}/coins/${mint}`;

  const response = await fetch(url);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`pump.fun API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Calculate bonding curve progress (0-100%)
 */
export function calculateBondingProgress(coin: PumpCoin): number {
  if (coin.complete) return 100;

  const TARGET_SOL = 85;
  const currentSol = coin.virtual_sol_reserves / 1e9;
  const INITIAL_VIRTUAL_SOL = 30;
  const realProgress = currentSol - INITIAL_VIRTUAL_SOL;
  const targetProgress = TARGET_SOL - INITIAL_VIRTUAL_SOL;

  const progress = (realProgress / targetProgress) * 100;
  return Math.min(Math.max(progress, 0), 100);
}

/**
 * Calculate price in SOL from bonding curve
 */
export function calculatePriceSol(coin: PumpCoin): number {
  if (coin.virtual_token_reserves === 0) return 0;
  return (coin.virtual_sol_reserves / 1e9) / (coin.virtual_token_reserves / 1e6);
}
